import React from "react";
import { ReactComponent as CombinedShape } from "../statics/icons/combined_shape.svg";
import CheckIcon from "@material-ui/icons/Check";
import { PackageId, PackageModel } from "./package";
import { ContextHelpModel, Risk, RiskModeId, RisksId } from "./risk";
import { Button, Checkbox } from "@material-ui/core";
import { RisksNetworkTypes } from "./risksNetwork";
import NumberInput from "../components/sharedComponents/numberInput/numberInput";
import Notification from "../components/sharedComponents/notification/notification";
import RefreshIcon from "@material-ui/icons/Refresh";
import Formatter from "./formatter";
import { ReactComponent as Info } from "../statics/icons/info.svg";
import { CalculateStore } from "../stores/calculateStore";
import { CalculationService } from "../services/services/calculationService";
import { PolicyStore } from "../stores/policyStore";
import { ReplenishmentHistoryStore } from "../stores/replenishmentHistoryStore";
import {NewLabel} from "../components/sharedComponents/newLabel/newLabel";

export default class PackagesContent {
    constructor(private calculateStore: CalculateStore, private calculationService: CalculationService,
        private setErrorMessage: (errorMessage: string) => void,
        private refreshPackage: (packageId: PackageId) => void, private infoIconOnClick: (riskId: RisksId) => void,
        private packagesDevice: any,
        private policyStore: PolicyStore,
        private replenishmentHistoryStore: ReplenishmentHistoryStore) {
    }

    public renderEditableRiskValue(packageId: PackageId, riskId: RisksId, disabledButtons: boolean): JSX.Element {
        return <>
            {this.renderCheckbox(packageId, riskId, disabledButtons)}
            {this.renderInsuranceSum(packageId, riskId, disabledButtons)}
        </>;
    }

    public renderRequiredRiskValue(packageId: PackageId, riskId: RisksId, disabledButtons: boolean): JSX.Element {
        return <>
            {this.renderCheckIcon()}
            {this.renderInsuranceSum(packageId, riskId, disabledButtons)}
        </>;
    }

    public renderCombinedShape(): JSX.Element {
        return <CombinedShape className="combined-shape" />;
    }

    public renderCheckIcon(): JSX.Element {
        return <CheckIcon className="union" color="primary" />;
    }

    public renderCheckbox(packageId: PackageId, riskId: RisksId, disabledButtons: boolean): JSX.Element {
        const risk = this.calculateStore.packages.get(packageId)!.risks.get(riskId)!;

        return (
            <Checkbox
                color="primary"
                checked={risk.available}
                className={`checkbox ${risk.hasError ? "has-error" : ""}`}
                onChange={(event) => {
                    this.checkboxOnChange(event.target.checked, packageId, riskId);
                }}
                disabled={disabledButtons}
            />
        );
    }

    public renderInsuranceSum(packageId: PackageId, riskId: RisksId, disabledButtons: boolean): JSX.Element | null {
        const packageData = this.calculateStore.packages.get(packageId)!;
        const risk = packageData.risks.get(riskId)!;
        const required = risk.risksNetworkValue === RisksNetworkTypes.required;
        const isRecalculating = packageData.calculationRequestStatus.isPending;

        if (!risk.hideInsuranceSumFromResponse) {
            if (risk.getInsuranceSumFromResponse) {
                if (risk.available || required) {
                    const isSuggestedInsuranceSum = !risk.insuranceSum && !!risk.suggestedInsuranceSum;
                    return (
                        <div className="insurance-sum-container">
                            <NumberInput formatted={true}
                                value={risk.insuranceSum ?? risk.suggestedInsuranceSum}
                                className={`insurance-sum ${isSuggestedInsuranceSum ? "suggested" : ""} ${risk.invalidInsuranceSum && !isRecalculating ? "invalid" : ""}`}
                                disabled={disabledButtons}
                                onChange={(value) => {
                                    this.onChangeInsuranceSum(riskId, value);
                                }}
                            /><span>zł*</span>
                        </div>
                    );
                }
            }
        }
        return null;
    }

    public renderPackageError(packageId: PackageId): JSX.Element {
        return <div className="error-wrapper">
            <Notification type="package-error" message="Błąd" />
            <RefreshIcon className="refresh-icon" onClick={() => {
                this.refreshPackage(packageId);
            }} />
        </div>;
    }

    public renderTotalAmountSpace(packageId: PackageId): JSX.Element | null {
        const response = this.calculateStore.packages?.get(packageId)?.calculationResponse;

        switch (response) {
            case null:
                return this.renderPackageError(packageId);
            case undefined:
                return null;
            default:
                return this.renderTotalAmount(packageId, response.totalAmount);
        }
    }

    public renderTotalAmount(packageId: PackageId, totalAmount: number): JSX.Element {
        const classStyle = this.calculateStore.packages.get(packageId)?.requireRecalculate ? "to-refresh" : "";

        return <>
            <div className={`total-amount ${classStyle}`}>
                {Formatter.decimalPriceFormat(Formatter.convertCentsToNormalNumber(totalAmount))}
            </div>
            <div className={`currency ${classStyle}`}>zł</div>
        </>;
    }

    public renderHeaders(): JSX.Element[] {
        const calculateStore = this.calculateStore!;
        const packages = calculateStore.packages;
        const headersContainer: JSX.Element[] = [];
        const shouldRenderTotalAmount = calculateStore.propertyInsurance;

        packages.forEach((packageData: PackageModel) => {
            headersContainer.push(
                <div key={`package-header-${packageData.id}`}
                    className={`header package-header package-header-${packageData.id}
                     ${this.isSelectedPackage(packageData.id) ? "selected-package" : ""}`}
                >
                    <div className="package-name">
                        {packageData.displayName}
                    </div>

                    <div className="package-desc">{packageData.displayDesc}</div>

          {shouldRenderTotalAmount && this.renderTotalAmountSpace(packageData.id)}
        </div>
      );
    });

        return headersContainer;
    }

    public renderContextHelpDisplayName(riskId: RisksId, riskContextHelps: any, contextHelpId: any, oddRow: boolean): JSX.Element {
        // @ts-ignore
        const description = riskContextHelps[contextHelpId];

        return (
            <div key={`${riskId}-${contextHelpId}`} className={`cell context-help-cell ${oddRow ? "odd" : ""}`}>
                <div className="item context-help-display-name">
                    {description}
                </div>
            </div>
        );
    }

    public renderAllValuesForContextHelp(riskId: RisksId, contextHelpId: any, oddRow: boolean): JSX.Element[] {
        const calculateStore = this.calculateStore!;
        const packages = calculateStore.packages;
        const cellContainer: JSX.Element[] = [];

        packages.forEach((packageData: PackageModel) => {
            const riskValue = packageData.risks.get(riskId);
            const contextHelp = this.findContextHelp(packageData, contextHelpId, riskId);
            const displayContextHelp = riskValue && riskValue.contextHelp && contextHelp;

            cellContainer.push(
                <div key={`${packageData.id}-${riskId}-${contextHelpId}`}
                    className={`cell package-cell context-help-cell ${this.isSelectedPackage(packageData.id) ? "selected-package" : ""} ${oddRow ? "odd" : ""}`}>
                    <div className="item">
                        {displayContextHelp ?
                            this.renderContextHelpValue(contextHelp!) :
                            this.renderCombinedShape()
                        }
                    </div>
                </div>
            );
        });

        return cellContainer;
    }

    public renderContextHelpValue(contextHelp: ContextHelpModel): JSX.Element {
        return <>
            {contextHelp.available && this.renderCheckIcon()}
            {contextHelp.additionalInfo && (
                <div className="context-help-additional-info">
                    {contextHelp.additionalInfo}
                </div>
            )}
        </>;
    }

    public renderAllValuesForRisk(riskId: RisksId, disabledButtons: boolean): JSX.Element[] {
        const cellContainer: JSX.Element[] = [];
        const calculateStore = this.calculateStore!;
        const packages = calculateStore.packages;

        packages.forEach((packageData: PackageModel) => {
            cellContainer.push(
                <div key={`${packageData.id}-${riskId}`}
                    className={`cell package-cell packageId_${packageData.id} riskId_${riskId} ${this.isSelectedPackage(packageData.id) ? "selected-package" : ""}`}>
                    <div className="item">
                        {this.renderRiskValue(packageData, riskId, disabledButtons)}
                    </div>
                </div>
            );
        });

        return cellContainer;
    }

    public renderRiskValue(packageData: PackageModel, riskId: RisksId, disabledButtons: boolean): JSX.Element {
        const riskValue = packageData.risks.get(riskId);

        if (riskValue) {
            const riskNetworkValue = riskValue.risksNetworkValue;
            const riskAvailable = riskNetworkValue === RisksNetworkTypes.available || (riskNetworkValue === undefined && riskValue.editable);
            const riskRequired = riskNetworkValue === RisksNetworkTypes.required || (riskNetworkValue === undefined && riskValue.available);

            if (riskAvailable) {
                return this.renderEditableRiskValue(packageData.id, riskId, disabledButtons);
            } else {
                if (riskRequired) {
                    return this.renderRequiredRiskValue(packageData.id, riskId, disabledButtons);
                } else {
                    return this.renderCombinedShape();
                }
            }
        } else {
            return this.renderCombinedShape();
        }
    }

    public renderRisksDisplayName(riskId: RisksId): JSX.Element {
        // @ts-ignore
        const displayName = Risk.risksInPackageDisplayName(riskId, this.calculateStore.estate.propertyType);

        return (
            <div key={riskId} className="cell insurance-name">
                <div className="item">
                    <div className="display-item-wrapper">
                        <div className="display-name">
                            {displayName}
                                <div className="icon">
                                    {this.calculateStore.propertyInsurance && this.renderInfoIcon(riskId)}
                                </div>
                        </div>
                        <div className="label">{riskId === RisksId.risksInOwu && <NewLabel/>}</div>
                        {this.getSuggestedInsuranceSum() &&
                            riskId === RisksId.detachedHouseOrFlatWithFixedElements &&
                            <div className="display-info">
                                Sugerowana Suma Ubezpieczenia: {this.getSuggestedInsuranceSum()} zł
                            </div>}
                    </div>
                </div>
            </div>
        );
    }

    private getSuggestedInsuranceSum() {
        // const suggestedInsuranceSum = this.calculateStore.packages.get(PackageId.mini)!.risks.get(RisksId.detachedHouseOrFlatWithFixedElements)!.suggestedInsuranceSum;
        const suggestedInsuranceSum = this.replenishmentHistoryStore!.responseCache!.suggestionNumber / 100;
        return Formatter.getFormattedNumber(suggestedInsuranceSum ?? "");
    }

    public renderInfoIcon(riskId: RisksId): JSX.Element {
        return <Info className="info-icon" onClick={this.infoIconOnClick.bind(this.packagesDevice, riskId)} />;
    }

    public renderSelectButtons(disabledButtons: boolean, position: string): JSX.Element[] {
        const calculateStore = this.calculateStore!;
        const cellContainer: JSX.Element[] = [];
        const packages = calculateStore.packages;

        packages.forEach((packageData: PackageModel) => {
            const isSelected = this.isSelectedPackage(packageData.id);
            const textButton = isSelected ? "wybrano" : "wybieram";

            cellContainer.push(
                <Button key={`select-button-${packageData.id}-${position}`}
                    variant="contained"
                    className={`select-button ${isSelected ? "selected-package-button selected-package" : ""}`}
                    onClick={() => {
                        this.changeSelectedPackage(packageData);
                    }}
                    disabled={disabledButtons}
                >
                    {textButton}
                </Button>);
        });

        return cellContainer;
    }

    public renderAmountInsuranceMessage() {
        return (
            <div className="amount-insurance-message">
                * Suma Ubezpieczenia
            </div>
        );
    }

    private changeSelectedPackage(packageData: PackageModel) {
        this.calculateStore!.selectPackage(packageData.id);
        this.calculationService!.setRisksNetwork(packageData);
    }

    public isSelectedPackage(packageId: PackageId): boolean {
        return this.calculateStore.selectedPackage === packageId;
    }

    public findContextHelp(packageData: PackageModel, contextHelpId: string, riskId: RisksId): ContextHelpModel | null | undefined {
        if (!packageData.risks.has(riskId)) {
            return null;
        }
        const contextHelps = packageData.risks.get(riskId)!.contextHelp;

        if (!contextHelps) {
            return null;
        }

        return contextHelps.find((x) => x.id === contextHelpId);
    }

    public tryCheckRequiredRiskForPackage(packageId: PackageId, riskId: RisksId) {
        if (this.calculateStore.packages.get(packageId)?.risks.get(riskId)?.editable) {
            this.setPackageRisk(packageId, riskId, true);
        }
        if (riskId === RisksId.movables && packageId === PackageId.maxi) {
            this.setPackageRisk(packageId, RisksId.detachedHouseOrFlatWithFixedElements, true);
        }
        if (this.calculateStore.noRequiredInsuranceProductsSelectedForPackage(packageId)) {
            this.setPackageRisk(packageId, RisksId.detachedHouseOrFlatWithFixedElements, true);
        }
    }

    public setPackageRisk(packageId: PackageId, riskId: RisksId, checked: boolean) {
        this.calculateStore.setPackageRisk(packageId, riskId, checked);

        if (checked) {
            this.selectOnlyFixedElementsOrDetachedHouseOrFlatWithFixedElements(packageId, riskId, checked);
        }

        this.validateAllPackages();

        if (this.isChangeFireAndRandomEventsCheckbox(riskId)) {
            const packageData = this.calculateStore.packages.get(packageId);

            if (packageData) {
                this.calculationService.setRisksNetwork(packageData);
            }
        }
    }

    public checkboxOnChange(checked: boolean, packageId: PackageId, riskId: RisksId) {
        if (checked) {
            Object.values(PackageId).forEach((otherPackageId: PackageId) => {
                if (this.calculateStore.noRequiredInsuranceProductsSelectedForPackage(otherPackageId)) {
                    this.tryCheckRequiredRiskForPackage(otherPackageId, riskId);
                }
            });
        }

        this.setPackageRisk(packageId, riskId, checked);
    }

    public selectOnlyFixedElementsOrDetachedHouseOrFlatWithFixedElements(packageId: PackageId, riskId: RisksId, checked: boolean) {
        if (riskId === RisksId.onlyFixedElements) {
            this.calculateStore.setPackageRisk(packageId, RisksId.detachedHouseOrFlatWithFixedElements, !checked);
        } else if (riskId === RisksId.detachedHouseOrFlatWithFixedElements) {
            this.calculateStore.setPackageRisk(packageId, RisksId.onlyFixedElements, !checked);
        }
    }

    public validatePackage(packageId: PackageId) {
        let error = "";
        this.setFireAndRandomEventsRisksError(packageId, false);

        const risks = this.calculateStore.packages.get(packageId)?.risks;
        if (!this.calculateStore.isPropertyUnderConstruction() && risks &&
            !risks.get(RisksId.onlyFixedElements)?.available &&
            !risks.get(RisksId.detachedHouseOrFlatWithFixedElements)?.available) {
            if (!risks.get(RisksId.movables)?.available) {
                this.setFireAndRandomEventsRisksError(packageId, true);
                error = "Musisz zaznaczyć conajmniej jedno z ryzyk:\n" +
                    "- Dom/Mieszkanie ze stałymi elementami,\n" +
                    "- Tylko stałe elementy bez domu/mieszkania,\n" +
                    "- Ruchomości domowe";
            } else if (risks.get(RisksId.movables)?.available && Risk.getRiskMode(risks) === RiskModeId.allRisk) {
                this.setEstateRisksError(packageId, true);
                error = "W pakiecie ALL RISK nie można ubezpieczyć samych ruchomości domowych.\n" +
                    "Musisz zaznaczyć jedno z ryzyk:\n" +
                    "- Dom/Mieszkanie ze stałymi elementami\n" +
                    "- Tylko stałe elementy bez domu/mieszkania\n";
            }
        }
        return error;
    }

    public validateAllPackages() {
        let error = "";

        this.calculateStore.packages.forEach((packageData: PackageModel) => {
            const packageError = this.validatePackage(packageData.id);
            error = packageError ? packageError : error;
        });

        this.setErrorMessage(error);
    }

    public setFireAndRandomEventsRisksError(packageId: PackageId, hasError: boolean) {
        this.setEstateRisksError(packageId, hasError);
        this.calculateStore.setErrorForPackageRisk(packageId, RisksId.movables, hasError);
    }

    public setEstateRisksError(packageId: PackageId, hasError: boolean) {
        this.calculateStore.setErrorForPackageRisk(packageId, RisksId.onlyFixedElements, hasError);
        this.calculateStore.setErrorForPackageRisk(packageId, RisksId.detachedHouseOrFlatWithFixedElements, hasError);
    }

    public isChangeFireAndRandomEventsCheckbox(riskId: RisksId) {
        switch (riskId) {
            case RisksId.onlyFixedElements:
            case RisksId.detachedHouseOrFlatWithFixedElements:
            case RisksId.movables:
                return true;
            default:
                return false;
        }
    }

    public onChangeInsuranceSum(riskId: RisksId, value: number) {
        this.calculateStore.setPackageInsuranceSum(PackageId.mini, riskId, value);
        this.calculateStore.setPackageInsuranceSum(PackageId.midi, riskId, value);
        this.calculateStore.setPackageInsuranceSum(PackageId.maxi, riskId, value);
    }
}
